import Navbar from '@/pages/home/components/Navbar';
import Footer from '@/pages/home/components/Footer';

const features = [
  {
    icon: 'ri-video-line',
    title: 'HD Video Lessons',
    description: 'Crystal-clear 4K video lectures with closed captions in 12 languages. Every lesson includes downloadable slides, code snippets, and a searchable transcript so you never miss a detail.',
    highlight: '4K Quality',
  },
  {
    icon: 'ri-code-box-line',
    title: 'Hands-On Projects',
    description: 'Apply what you learn immediately with real-world projects reviewed by industry mentors. Build a portfolio that hiring managers actually care about — not another todo app.',
    highlight: '4+ Projects per Course',
  },
  {
    icon: 'ri-award-line',
    title: 'Verified Certificates',
    description: 'Earn a shareable, blockchain-verified certificate for every course you complete. Add it to your LinkedIn profile with one click and stand out to recruiters.',
    highlight: 'LinkedIn Ready',
  },
  {
    icon: 'ri-infinity-line',
    title: 'Lifetime Access',
    description: 'Pay once and you own the course forever. Revisit any lesson as AI evolves — we continuously update our content and you get every update at no extra cost.',
    highlight: 'Free Updates Forever',
  },
  {
    icon: 'ri-message-3-line',
    title: 'Community & Q&A',
    description: 'Join 36,000+ learners in our private Discord community. Ask questions, share projects, and get feedback from instructors within 24 hours — guaranteed.',
    highlight: '24hr Response Time',
  },
  {
    icon: 'ri-user-star-line',
    title: 'Expert Instructors',
    description: 'Learn from AI researchers and engineers at OpenAI, Google DeepMind, Anthropic, and leading startups. They bring real production experience, not just theory.',
    highlight: 'Industry Veterans',
  },
];

const stats = [
  { value: '36,000+', label: 'Students Enrolled' },
  { value: '4.85', label: 'Average Rating' },
  { value: '240+', label: 'Hours of Content' },
  { value: '98%', label: 'Satisfaction Rate' },
];

export default function FeaturesPage() {
  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero */}
      <section className="py-16 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-sm font-semibold text-primary-500 uppercase tracking-widest mb-3">
            Why ggcourse
          </p>
          <h1 className="text-3xl md:text-4xl font-heading font-bold text-foreground-950 mb-4">
            Built for Learners, Designed for Results
          </h1>
          <p className="text-base text-foreground-600 max-w-2xl mx-auto">
            Every aspect of our platform is engineered to maximize your learning outcomes — from video quality to community support.
          </p>
        </div>
      </section>

      {/* Stats Strip */}
      <section className="border-b border-background-200 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat) => (
              <div key={stat.label} className="text-center">
                <p className="text-2xl md:text-3xl font-heading font-bold text-primary-500">{stat.value}</p>
                <p className="text-sm text-foreground-500 mt-1">{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Cards */}
      <section className="py-16 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature) => (
              <div
                key={feature.title}
                className="rounded-xl border border-background-200 bg-background-50 p-6 transition-colors hover:border-primary-300 group"
              >
                <div className="w-12 h-12 rounded-xl bg-primary-100 flex items-center justify-center mb-4 group-hover:bg-primary-200 transition-colors">
                  <i className={`${feature.icon} text-primary-600 text-xl w-5 h-5 flex items-center justify-center`} />
                </div>

                <div className="flex items-center gap-2 mb-2">
                  <h3 className="font-heading font-bold text-foreground-950">{feature.title}</h3>
                  <span className="inline-flex w-fit shrink-0 items-center justify-center gap-1 overflow-hidden rounded-full border border-transparent px-2 py-0.5 font-medium whitespace-nowrap transition-colors bg-primary-100 text-primary-700 text-xs">
                    {feature.highlight}
                  </span>
                </div>

                <p className="text-sm text-foreground-600 leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-16 bg-primary-500">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mb-3">
            Ready to experience it yourself?
          </h2>
          <p className="text-sm text-primary-100 mb-6 max-w-md mx-auto">
            Browse our course catalog and see why 36,000+ students chose ggcourse for their AI education.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
            <Link
              to="/courses"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-background-50 text-primary-600 hover:bg-background-100 h-10 px-5 text-sm"
            >
              Browse Courses
              <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center" />
            </Link>
            <Link
              to="/pricing"
              className="inline-flex shrink-0 items-center justify-center gap-2 rounded-md font-medium whitespace-nowrap transition-all outline-none bg-primary-600 text-background-50 hover:bg-primary-700 h-10 px-5 text-sm"
            >
              View Pricing
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}